import React, { useCallback, useContext } from 'react'

import classNames from 'clsx'
import memoizeOne from 'memoize-one'
import PropTypes from 'prop-types'
import { omit, pick } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import {
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import { makeStyles } from '@mui/styles'

import queryString from 'query-string'

import { getFirstImage } from '~/src/Annotations/Formatters/utils'
import AnnotationIcon from '~/src/Annotations/Icon'
import { annotationItemsShape } from '~/src/Annotations/shapes'
import { schemaFormatters, Tooltipper as Tooltip } from '~/src/Annotations/utils'
import { NO_MORE_ITEMS } from '~/src/Journal/constants'
import getGlobal from '~/src/Lib/getGlobal'
import { idAndNameShape, idPropType, stylePropType } from '~/src/Lib/PropTypes'
import {
  EMPTY_OBJECT,
  getDateTime,
  shallowEqualsMemoizer,
  useFilestackURL,
} from '~/src/Lib/Utils'
import { shape as roomShape } from '~/src/Room/bundle'
import { ChartContext as RoomContext } from '~/src/Room/Context'
import ImageCard from '~/src/UI/Cards/Image'

const ITEM_DATE_FORMAT = { month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' }
const defaultItemFields = ['date', 'location', 'user']
const itemFieldsWithBody = ['body', ...defaultItemFields]
const ITEM_FIELDS = {
  default: defaultItemFields,
  harvestPhases: itemFieldsWithBody,
  readings: itemFieldsWithBody,
  targetRanges: itemFieldsWithBody,
  notifications: ['body', 'date', 'location'],
  pesticideApplications: itemFieldsWithBody,
  notes: itemFieldsWithBody,
}
const ITEM_ADDITIONAL_PROPS = {
  notes: { inline: true }
}
/* eslint-disable babel/camelcase */
const ITEM_ICON_COLORS = {
  warning: 'error',
  check_box_outline_blank: 'primary',
  check_box: 'primary',
}
/* eslint-enable babel/camelcase */
const displayName = 'Journal/Item'

const AVATAR_SIZE = 32

export const styles = theme => ({
  itemRoot: {
    padding: theme.spacing(0.5, 0)
  },
  avatar: {
    minWidth: AVATAR_SIZE + 14,
    paddingLeft: 8,
    '& > div': {
      padding: 0,
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
    },
  },
  icon: {
    fill: theme.palette.gray,
    color: theme.palette.gray,
  },
  // annotation formatter classes
  root: {
    display: 'grid',
    height: '100%',
    width: '100%',
    gridRowGap: theme.spacing(0.5),
    gridTemplateAreas: [
      '"content   content content"',
      '"location  date    date"'
    ].join('\n'),
    gridTemplateColumns: `1fr auto ${AVATAR_SIZE + (theme.spacingValue * 2)}`,
    gridTemplateRows: 'auto',
    alignItems: 'center',
    '&$notes $body, &$targetRanges $body, &$pesticideApplications $body': {
      flex: '0 0 100%',
      paddingLeft: 0,
      ...theme.typography.body2
    },
    '&$notes $content, &$notifications $content, &$targetRanges $content, &$pesticideApplications $content': {
      flexWrap: 'wrap',
    },
    '&$notifications $body': {
      display: 'inline-flex',
      alignContent: 'center',
      alignItems: 'center',
      flex: '0 0 100%',
      height: 'auto',
      padding: 0,
      textAlign: 'left',
    },
    '&$notifications $title, &$targetRanges $title, &$pesticideApplications $title': {
      flex: '0 0 100%',
      lineHeight: 1.3,
      '& span': {
        lineHeight: 'inherit',
      },
    },
    '&$showUser': {
      gridTemplateAreas: [
        '"content   content   content user"',
        '"location  date  date    date"'
      ].join('\n'),
      gridTemplateColumns: `5fr 2fr 2fr ${AVATAR_SIZE + (theme.spacingValue * 2)}px`,
    },
    '&$readings': {
      '& $title': {
        flex: '1 1 max-content',
      },
      '& $body': {
        flex: '0 2 max-content',
      },
    },
    '&$tasks $content': {
      flexWrap: 'wrap',
    },
    '&$tasks $title': {
      flex: '0 2 auto',
      '& > p': {
        fontSize: 11.5,
      }
    },
    '&$tasks $body:not(:empty)': {
      width: '100%',
      paddingLeft: 0,
      paddingRight: theme.spacing(1),
    },
  },
  inlineRoot: {},
  content: {
    gridArea: 'content',
    display: 'flex',
    alignItems: 'flex-start',
    overflow: 'hidden',
    '& *': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }
  },
  title: {
    flex: '0 1 auto',
    '&:empty': {
      display: 'none',
    }
  },
  body: {
    display: 'none',
    '&:not(:empty)': {
      height: 20,
      padding: theme.spacing(0.25, 1, 0),
      display: 'block',
      flex: '1 1 auto',
    }
  },
  date: {
    gridArea: 'date',
    paddingRight: theme.spacing(),
    fontSize: 11,
    textAlign: 'right',
    textTransform: 'lowercase',
    width: 'initial',
    display: 'block',
    '$root &': {
      textAlign: 'right',
      width: '100%',
    }
  },
  location: {
    gridArea: 'location',
    width: 'initial',
    textAlign: 'left',
  },
  user: {
    gridArea: 'user',
    display: 'none',
    '&:not(:empty)': {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: theme.spacing(0, 1),
      minWidth: theme.spacing(6),
      height: theme.spacing(4),
    }
  },
  notes: {},
  notifications: {
    gridTemplateAreas: [
      '"content   content   content"',
      '"location  location  date"'
    ].join('\n'),
    gridTemplateColumns: 'repeat(2, 1fr) auto'
  },
  pesticideApplications: {},
  readings: {},
  targetRanges: {},
  tasks: {},
  showUser: {},
  tooltip: {
    display: 'flex',
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.palette.background.default,
    boxShadow: theme.shadows[5],
    minWidth: 200,
  },
  imageCard: {
    background: theme.palette.background.default,
    '&$imageCardTooltip': {
      margin: theme.spacing(2, 1),
      height: 'auto',
      width: '100%',
      overflow: 'hidden',
      '& $imageCardMedia': {
        maxHeight: 'inherit',
      }
    }
  },
  imageCardTooltip: {},
  imageCardMedia: {},
})

const getAnnotationClasses = memoizeOne(pick([
  'root',
  'inlineRoot',
  'title',
  'body',
  'date',
  'location',
  'user',
]))
const getImageCardClasses = memoizeOne(({
  imageCard: root,
  imageCardTooltip: tooltip,
  imageCardMedia: media,
}) => ({
  root,
  tooltip,
  media,
}))
const getTooltipClasses = memoizeOne(pick(['tooltip']))

const useStyles = makeStyles(styles, { name: displayName })

const attachmentOmitter = omit(['attachment'])

export const buildURL = ({ room }, { uuid: annotation, timestamp }) => {
  const ts = getDateTime(timestamp)
  const from = ts.minus({ hours: 3 }).toISO()
  const to = ts.plus({ hours: 3 }).toISO()
  const query = { annotation, from, to, individualSensors: true }
  const { location } = getGlobal()

  return location.href.includes('/rooms/') ? null : `/rooms/${room}?${queryString.stringify(query)}`
}

const NoMoreItems = ({ before, className, style }) => (
  <ListItem className={className} style={style}>
    <ListItemText
      align="center"
      primary={`${before ? 'Beginning' : 'End'} of Journal`}
    />
  </ListItem>
)
NoMoreItems.propTypes = {
  className: PropTypes.string.isRequired,
  before: PropTypes.bool.isRequired,
  style: stylePropType.isRequired,
}

const AnnotationWrapper = React.memo(({ children, classes, schema, user }) => {
  const [title, body, ...rest] = children

  return (
    <div className={classNames(classes.root, classes[schema], user && classes.showUser)}>
      <div className={classes.content}>
        {title}
        {body}
      </div>
      {rest}
    </div>
  )
})
AnnotationWrapper.propTypes = {
  children: PropTypes.arrayOf(PropTypes.element).isRequired,
  schema: PropTypes.string.isRequired,
  user: PropTypes.shape({ firstName: PropTypes.string, lastName: PropTypes.string }),
}
AnnotationWrapper.defaultProps = {
  user: null,
}

const getWrapper = memoizeOne((classes, schema) => props => <AnnotationWrapper {...props} classes={classes} schema={schema} />)

// eslint-disable-next-line react/require-default-props
const Item = props => {
  const {
    // parent props & context
    activeRoom,
    rooms,
    zones,
    users,
    // item props
    data,
    index,
    style,
    ...passthruProps
  } = props
  const classes = useStyles(props)
  const item = data[index]
  const { uuid, data: annotation, timestamp } = item
  const [schema] = uuid.split('_')
  const { [schema]: formatters } = schemaFormatters
  const { tooltip: Formatter } = formatters ?? EMPTY_OBJECT
  const thumbnail = getFirstImage(annotation?.attachment)
  let openCloseProps
  if (formatters?.onOpen || formatters?.onClose) {
    openCloseProps = { ...passthruProps, annotation }
  }

  const { onOpen, onClose } = formatters ?? EMPTY_OBJECT
  const onActivate = useCallback(event => {
    if (!onOpen) return
    event.preventDefault()
    onOpen(openCloseProps)
  }, [onOpen, openCloseProps])
  const onDeactivate = useCallback(event => {
    if (!onClose) return
    event.preventDefault()
    onClose(openCloseProps)
  }, [onClose, openCloseProps])
  const Wrapper = getWrapper(classes, schema)
  const filestackURL = useFilestackURL()
  const isNoMoreItems = item.data === NO_MORE_ITEMS

  if (!isNoMoreItems && (!item || !Formatter)) {
    console.warn('[Journal/Item] no item for index:', { data, index, item })
    return null
  }

  if (isNoMoreItems) {
    return (
      <NoMoreItems {...props} before={index !== 0} className={classes.itemRoot} />
    )
  }
  const fields = ITEM_FIELDS[schema] ?? ITEM_FIELDS.default

  const href = schema === 'notifications' && annotation?.room ? buildURL(annotation, item) : `#/${schema}/${annotation.id}`

  return Formatter ? (
    <ListItem
      button={Boolean(href)}
      data-testid={`journal-item-${uuid}`}
      disableGutters
      className={classes.itemRoot}
      style={style}
      component={href ? 'a' : 'div'}
      href={href}
      onMouseEnter={onActivate}
      onMouseLeave={onDeactivate}
      onFocus={onActivate}
      onBlur={onDeactivate}
    >
      {!thumbnail ? (
        <ListItemIcon style={{ paddingLeft: 12, minWidth: 46 }}>
          <AnnotationIcon
            {...item}
            schema={schema}
            annotation={annotation}
            className={ITEM_ICON_COLORS[item.icon] ? undefined : classes.icon}
            color={ITEM_ICON_COLORS[item.icon] ?? 'inherit'}
            fontSize="default"
          />
        </ListItemIcon>
      ) : (
        <ListItemAvatar className={classes.avatar}>
          <Tooltip
            classes={getTooltipClasses(classes)}
            placement="left"
            title={(
              <ImageCard
                {...thumbnail}
                tooltip
                classes={getImageCardClasses(classes)}
              />
            )}
          >
            <Avatar
              alt={annotation?.title ?? 'n/a'}
              src={filestackURL(thumbnail, ['resize=width:32,height:32,fit:crop,align:center', 'circle'])}
            />
          </Tooltip>
        </ListItemAvatar>
      )}
      <Formatter
        {...ITEM_ADDITIONAL_PROPS[schema]}
        color="textSecondary"
        annotation={thumbnail ? attachmentOmitter(annotation) : annotation}
        annotationKey={uuid}
        avatarSize={AVATAR_SIZE}
        classes={getAnnotationClasses(classes, schema)}
        date={timestamp}
        dateFormat={ITEM_DATE_FORMAT}
        fields={fields}
        room={!activeRoom ? rooms[annotation.room] : undefined}
        zone={zones[annotation.zone]}
        user={users[item.relatedUser]}
        Wrapper={Wrapper}
      />
    </ListItem>
  ) : null
}
Item.propTypes = {
  activeRoom: roomShape,
  rooms: PropTypes.objectOf(idAndNameShape).isRequired,
  zones: PropTypes.objectOf(idAndNameShape).isRequired,
  users: PropTypes.objectOf(PropTypes.shape({ id: idPropType, fullName: PropTypes.string })).isRequired,
  data: annotationItemsShape.isRequired,
  index: PropTypes.number.isRequired,
  style: stylePropType.isRequired,
}
Item.defaultProps = {
  activeRoom: null,
}

Item.displayName = displayName

const contextKeys = ['setActiveNotification']
const contextPicker = pick(contextKeys)

export default shallowEqualsMemoizer((outerProps, areEqual) => React.memo(props => {
  const connectedProps = useConnect(
    'selectMe',
    'selectRooms',
    'selectZones',
    'selectUsers'
  )
  const context = useContext(RoomContext)

  return (
    <Item
      {...connectedProps}
      {...outerProps}
      {...contextPicker(context)}
      {...props}
    />
  )
}, areEqual))
